import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getPermissionsInfo, getCountries } from '../api/admin';

// Country Control & Self-Service architecture — country selector.
// A dedicated context/state (not per-page): "the selection is global and
// filters every list, metric and counter." An admin can now be granted
// MULTIPLE countries (admin_country_access is a real many-to-many table,
// replacing the earlier single admin_roles.country_code model) — a
// restricted admin only ever sees their own granted countries plus (if they
// have more than one) "All countries" among THEIR OWN; an unrestricted
// admin (company_director/super_admin) sees every country.
const CountryContext = createContext(null);
// Per-admin, so one admin's last choice never leaks into another's session on
// the same browser. The key is only a convenience; the server still enforces
// which countries an admin may actually see.
const storageKeyFor = (userId) => `nula_admin_selected_country:${userId}`;

export function CountryProvider({ children }) {
  const { user } = useAuth();
  const [isUnrestricted, setIsUnrestricted] = useState(true);
  const [grantedCodes, setGrantedCodes] = useState([]); // country codes this admin is granted (restricted admins only)
  // Live, active countries from country_regions — NOT hardcoded, so a country
  // the Owner adds via Countries & Settings shows up here with no code change.
  const [activeCountries, setActiveCountries] = useState([]); // [{country_code, country_name}]
  // Owner / Global Admin only: paused countries they can still pick, so a
  // paused market can be reviewed and moderated (Kayd, 18.10.2026: with US
  // and KE both paused the selector offered nothing but "All countries",
  // which is read-only, so listings could not be approved at all).
  const [pausedCountries, setPausedCountries] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState('ALL');

  useEffect(() => {
    if (!user) return;
    try { setSelected(localStorage.getItem(storageKeyFor(user.id)) || 'ALL'); } catch (_) { setSelected('ALL'); }
    // Loaded independently: GET /countries is forbidden for restricted
    // accounts, and with Promise.all that one 403 used to discard the
    // permissions response too, leaving isUnrestricted at its initial true
    // ("All countries" shown to a contractor). permissions-info now carries
    // the admin's own active countries; GET /countries is only a fallback.
    Promise.allSettled([getPermissionsInfo(), getCountries()]).then(([permRes, countriesRes]) => {
      const perm = permRes.status === 'fulfilled' ? permRes.value.data : null;
      // Never grant anything on failure: no permissions answer = restricted, no countries.
      const unrestricted = !!perm && (perm.countryCodes === null || perm.countryCodes === undefined);
      setIsUnrestricted(unrestricted);
      setGrantedCodes(perm?.countryCodes || []);
      const fromPerm = perm?.activeCountries;
      const fromList = countriesRes.status === 'fulfilled' ? (countriesRes.value.data?.countries || []) : [];
      setActiveCountries((Array.isArray(fromPerm) ? fromPerm : fromList).filter((c) => c.status === 'active'));
      setPausedCountries(unrestricted ? (perm?.manageableCountries || []).filter((c) => c.status === 'paused') : []);
      setLoaded(true);
    });
  }, [user]);

  const labelFor = (code) => activeCountries.find((c) => c.country_code === code)?.country_name
    || pausedCountries.find((c) => c.country_code === code)?.country_name || code;
  const pausedCodes = useMemo(() => pausedCountries.map((c) => c.country_code), [pausedCountries]);

  // This admin's own usable country codes: everyone (ALL) if unrestricted,
  // otherwise only the ones they've been granted.
  const myCodes = useMemo(() => (isUnrestricted ? activeCountries.map((c) => c.country_code) : grantedCodes.filter((c) => activeCountries.some((a) => a.country_code === c))), [isUnrestricted, grantedCodes, activeCountries]);

  // Fix country selector (Permissions & History Development Plan,
  // 13.10.2026): "An outdated or paused saved value must not block the view
  // permanently." The bug was here -- "All countries" was only ever
  // offered when 2+ INDIVIDUAL countries happened to be active, so an
  // Owner got stuck on "NONE" with literally no way back the moment active
  // countries dropped to 1 or 0 (e.g. US paused, only KE left). For an
  // Owner/Global Admin, "All countries" means "don't restrict me" and must
  // always be a valid fallback, independent of how many individual
  // countries are currently active. A restricted admin with genuinely zero
  // released countries has nothing to fall back to -- "NONE" + the clear
  // warning message is correct and intentional for them, not a bug.
  // Everything this admin may pick: their usable countries, plus -- for the
  // Owner / Global Admin only -- paused countries. Never wider than that.
  const selectableCodes = useMemo(() => (isUnrestricted ? [...myCodes, ...pausedCodes] : myCodes), [isUnrestricted, myCodes, pausedCodes]);

  useEffect(() => {
    if (!loaded) return;
    const validSelections = isUnrestricted ? [...selectableCodes, 'ALL'] : myCodes;
    if (!validSelections.includes(selected)) {
      setSelected(isUnrestricted ? 'ALL' : (myCodes[0] || 'NONE'));
    }
  }, [loaded, myCodes, selectableCodes, isUnrestricted]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCountry = (code) => {
    const allowed = selectableCodes.includes(code) || (code === 'ALL' && (isUnrestricted || myCodes.length > 1));
    if (!allowed) return; // silently refuse — the UI shouldn't offer this anyway
    setSelected(code);
    if (user) { try { localStorage.setItem(storageKeyFor(user.id), code); } catch (_) { /* private mode */ } }
  };

  const availableOptions = useMemo(() => {
    const opts = myCodes.map((code) => ({ code, label: labelFor(code) }));
    // Owner/Global Admin always gets "All countries", even with 0 or 1
    // active countries right now. A restricted admin only gets it once
    // they have 2+ of their OWN granted countries -- otherwise it would be
    // a meaningless choice identical to their only option.
    if (isUnrestricted || opts.length > 1) opts.unshift({ code: 'ALL', label: isUnrestricted ? 'All countries' : 'All countries (yours)' });
    if (isUnrestricted) pausedCodes.forEach((code) => opts.push({ code, label: `${labelFor(code)} (paused)`, paused: true }));
    return opts;
  }, [myCodes, isUnrestricted, activeCountries, pausedCodes]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    country: selected,
    setCountry,
    isAllCountries: selected === 'ALL',
    isReadOnly: selected === 'ALL', // "In All countries mode: read and reporting only, no write actions."
    availableOptions,
    locked: !isUnrestricted,
    isUnrestricted,
    grantedCountries: myCodes,
    isPausedCountry: pausedCodes.includes(selected),
    // True once the saved selection and the admin's countries are loaded.
    // Country-filtered pages wait for it, so a page refresh never briefly
    // shows "All countries" results before the saved country applies.
    ready: loaded,
  };

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
}

export function useCountry() {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error('useCountry must be used within a CountryProvider');
  return ctx;
}
